import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import { makeStyles } from '@material-ui/core/styles';
import classnames from 'classnames';
import React, { useRef } from 'react';

import { color } from '../common/themes';
import useResponsiveScale from './hooks/useResponsiveScale';

interface AutocompleteMenuProps {
	open: boolean;
	suggestions: string[];
	focusedIndex: number;
	inputValue: string;
	onSelect: (item: string) => void;
	onClose: () => void;
	onFocusedChange: (index: number) => void;
}

const useStyles = makeStyles(
	theme => ({
		menu: {
			position: 'absolute',
			bottom: '100%',
			left: 0,
			right: 0,
			backgroundColor: '#FFFFFF',
			border: `1px solid ${color.$8}`,
			borderRadius: '8px',
			boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)',
			maxHeight: 320,
			overflow: 'auto',
			padding: '4px 0',
			zIndex: 1300,
			fontFamily: theme.typography.fontFamily,
		},
		item: {
			display: 'flex',
			alignItems: 'center',
			padding: '12px 20px',
			cursor: 'pointer',
			fontSize: ({ itemFontSize }: { itemFontSize: number }) => itemFontSize,
			lineHeight: 1.4,
			color: color.$3,
			transition: 'background-color 0.12s ease',
			'&:hover': { backgroundColor: '#EFEFEF' },
			'&.focused': { backgroundColor: '#EFEFEF' },
		},
		highlight: {
			font: 'inherit',
			fontWeight: 'bold',
		},
	}),
	{
		name: 'AutocompleteMenu',
	}
);

const AutocompleteMenu = ({
	open,
	suggestions,
	focusedIndex,
	inputValue,
	onSelect,
	onClose,
	onFocusedChange,
}: AutocompleteMenuProps) => {
	// 依使用者的 fontSizeLevel（small/medium/large）取得縮放後的字級
	const { fontSize: itemFontSize } = useResponsiveScale();
	const classes = useStyles({ itemFontSize });
	const menuRef = useRef<HTMLDivElement>(null);
	const keyCounter = useRef(0);

	if (!open || !suggestions || suggestions.length === 0) {
		return null;
	}

	const getCurrentKey = () => {
		const key = keyCounter.current;
		keyCounter.current = key + 1;
		return key;
	};

	return (
		<ClickAwayListener onClickAway={onClose}>
			<div ref={menuRef} className={classes.menu}>
				{suggestions.map(suggestion => {
					const itemKey = `suggestion-${getCurrentKey()}`;
					return (
						<div
							key={itemKey}
							className={classnames(classes.item, {
								focused: suggestion === suggestions[focusedIndex],
							})}
							role="presentation"
							onMouseEnter={() => onFocusedChange(suggestions.indexOf(suggestion))}
							onClick={() => onSelect(suggestion)}
						>
							{(() => {
								if (!inputValue || inputValue.length < 2) {
									return suggestion;
								}

								const escapeRegExp = (str: string): string => {
									return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
								};

								const regex = new RegExp(`(${escapeRegExp(inputValue)})`, 'gi');
								const parts = suggestion.split(regex);

								return (
									<>
										{parts.map((part, partIndex) => {
											const partKey = `${itemKey}-part-${partIndex}`;
											return regex.test(part) ? (
												<span key={partKey} className={classes.highlight}>
													{part}
												</span>
											) : (
												<span key={partKey}>{part}</span>
											);
										})}
									</>
								);
							})()}
						</div>
					);
				})}
			</div>
		</ClickAwayListener>
	);
};

export default AutocompleteMenu;
